import { css, styled } from 'twin.macro';
import MetricsSection from '../MetricsSection';
import EmployeeVisitTable from '../EmployeeVisitTable';
import DigitalClock from '@/components/Features/DateTime/DigitalClock';
import { convertDateToISO, getStandardDate, getTeamIdFromURL } from '@/_helpers';
import { useEffect, useMemo, useState } from 'react';
import { BsPeople, BsCardChecklist } from 'react-icons/bs';
import DateTimePicker from 'react-datetime-picker/dist/DateTimePicker';
import { FaRegCalendarAlt } from 'react-icons/fa';
import SearchByUserEmployee from '@/components/Admin/EVVContainer/EvvSearchModal/SearchByUserEmployee';
import { ImFilesEmpty } from 'react-icons/im';
import { connect } from 'react-redux';
import { clearDashboard, getTeamSummary, getTeamVisits } from '@/_redux/actions/ActionDashboard';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import MileageBarChart from '../MileageBarChart';
import SearchByClient from '@/components/Admin/EVVContainer/EvvSearchModal/SearchByClient';
import DashboardChart from '../DashboardChart';
import ChartsWithValidation from '../Components/ChartsWithValidation';

const summaryConfig = [
    { key: 'totalEmployees', title: 'TOTAL STAFFS', icon: BsPeople, bg: '#6c5ce7' },
    { key: 'totalFormsSubmitted', title: 'FORMS SUBMITTED', icon: ImFilesEmpty, bg: '#f1c40f' },
    { key: 'totalVisits', title: 'VISITS RECORDED', icon: BsCardChecklist, bg: '#1abc9c' },
    { key: 'totalSchedules', title: 'SCHEDULES', icon: FaRegCalendarAlt, bg: '#14a5e0' },
];

const DashboardContainer = styled.div`
    padding: 20px;
    color: black;
    position: relative;
`;

const SectionContainer = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
    margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
    font-size: 1.4rem;
    font-weight: 600;
    color: #5f6060;
    margin-bottom: 20px;
`;

const FiltersDiv = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const Dashboard = ({ teams, getTeamSummary, getTeamVisits, teamDashboardSummary, clearDashboard }) => {
    const history = useHistory();
    const teamId = getTeamIdFromURL();
    const [currentPage, setCurrentPage] = useState(1);
    const [userEmployee, setUserEmployee] = useState();
    const [client, setClient] = useState();
    const [day, setDay] = useState(new Date());
    const today = new Date();
    const dateOnly = getStandardDate(today, false);
    const initialStartDate = convertDateToISO(dateOnly, { start: true });
    const initialEndDate = convertDateToISO(dateOnly, { end: true });
    const handleUserEmployeeSelect = (selectedUser) => {
        setUserEmployee(selectedUser);
    };
    const [filters, setFilters] = useState({
        startDate: initialStartDate,
        endDate: initialEndDate,
    });
    const team = useMemo(() => {
        return (teams || []).find((team) => team.id === teamId) || {};
    }, [teams, teamId]);
    const providerId = useMemo(() => (team?.provider || {})?._id || null, [team]);

    useEffect(() => {
        clearDashboard();
    }, []);

    useEffect(() => {
        if (team && team?._id) {
            if (providerId) {
                getTeamSummary({
                    team: teamId,
                    providerId,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                });
            } else {
                history.push('/members');
            }
        }
    }, [team, filters]);

    useEffect(() => {
        if (team && team?._id && providerId) {
            getTeamVisits({
                page: currentPage,
                team: teamId,
                startDate: filters.startDate,
                endDate: filters.endDate,
                provider: providerId,
                employee: userEmployee ? userEmployee?.id : undefined,
                client: client ? client?.id : undefined,
            });
        }
    }, [team, filters, currentPage, userEmployee, client]);

    const metricsData = useMemo(() => {
        return summaryConfig.map(({ key, title, icon: Icon, bg }) => ({
            title,
            value: teamDashboardSummary?.[key] ?? 0,
            icon: <Icon size={20} />,
            iconStyles: { backgroundColor: bg },
        }));
    }, [teamDashboardSummary]);

    const onPageChanged = (data) => {
        if (data.currentPage < 1) {
            data.currentPage = 1;
        }
        setCurrentPage(data.currentPage);
    };

    const handleClientSelect = (selectedClient) => {
        setClient(selectedClient);
    };
    if (!team || !team?._id) {
        return <div>Loading...</div>;
    }

    return (
        <DashboardContainer>
            <MetricsSection metrics={metricsData} loading={teamDashboardSummary?.loading} />
            <SectionContainer>
                <FiltersDiv>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div
                            css={css`
                                width: 300px;
                            `}
                        >
                            {teamId && providerId && (
                                <SearchByClient
                                    client={client}
                                    handleSelect={handleClientSelect}
                                    provider={{ ...(team?.provider || {}), value: providerId }}
                                    disabled={!teamId || !providerId}
                                />
                            )}
                        </div>
                        <div
                            css={css`
                                width: 300px;
                            `}
                        >
                            {teamId && providerId && (
                                <SearchByUserEmployee
                                    userEmployee={userEmployee}
                                    handleSelect={handleUserEmployeeSelect}
                                    disabled={!teamId || !providerId}
                                    team={teamId}
                                    provider={{ ...(team?.provider || {}), value: providerId }}
                                />
                            )}
                        </div>

                        <div
                            style={{
                                borderRadius: '5px',
                                backgroundColor: 'white',
                                width: 'fit-content',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <div>Select Day:</div>
                            <DateTimePicker
                                onChange={(date) => {
                                    if (!date) {
                                        setFilters((prev) => ({ ...prev, endDate: null }));
                                        return;
                                    }
                                    const dateOnly = getStandardDate(date, false);
                                    const startDate = convertDateToISO(dateOnly, { start: true });
                                    const endDate = convertDateToISO(dateOnly, { end: true });
                                    setFilters((prev) => ({ ...prev, startDate, endDate }));
                                    setDay(date);
                                }}
                                value={day}
                                format="MM-dd-y"
                                disableClock={true}
                                calendarType={'US'}
                                maxDate={new Date()}
                                clearIcon={null}
                            />
                        </div>
                    </div>

                    <div>
                        <DigitalClock />
                    </div>
                </FiltersDiv>
            </SectionContainer>

            <SectionContainer>
                <SectionTitle>Employee Visit Table</SectionTitle>
                <EmployeeVisitTable currentPage={currentPage} onPageChanged={onPageChanged} />
            </SectionContainer>

            <SectionContainer>
                <SectionTitle>Mileage Info By Employee</SectionTitle>
                <MileageBarChart />
            </SectionContainer>
            <DashboardChart />
            <div className="mt-4"></div>
            <ChartsWithValidation />
        </DashboardContainer>
    );
};

const mapStateToProps = (state) => ({
    teamDashboardSummary: state.dashboard.teamSummary || {},
    teamDashboardVisits: state.dashboard.teamVisit || {},
    teams: state.teams.teams,
});

export default connect(mapStateToProps, { getTeamSummary, getTeamVisits, clearDashboard })(Dashboard);
